import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../utils/api';

const AuthContext = createContext(null);

/**
 * Helper function to get user's primary role from user object
 * This function doesn't rely on context state, so it can be used
 * immediately after login without race conditions.
 */
export const getRoleFromUser = (user) => {
  if (!user) return null;

  // Check lawyer first - superusers with lawyer role go to lawyer dashboard
  if (user.is_lawyer) return 'lawyer';
  if (user.is_client) return 'client';
  // Enduser gets their own tools dashboard
  if (user.is_enduser) return 'enduser';
  // Staff/superusers without specific roles default to lawyer dashboard
  if (user.is_staff || user.is_superuser) return 'lawyer';
  if (user.is_affiliate) return 'affiliate';

  return null;
};

/**
 * Helper function to check if user has access to a specific feature
 * Staff/superusers always have access to all features.
 */
export const hasFeature = (user, featureCode) => {
  if (!user) return false;
  // Staff and superusers have access to all features
  if (user.is_staff || user.is_superuser) return true;
  // Check user's features array
  return user.features?.includes(featureCode) ?? false;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Initialize auth state from localStorage
   */
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('user');

      // Set state SYNCHRONOUSLY first to prevent race conditions
      if (storedToken && storedUser) {
        setToken(storedToken);
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
        } catch (e) {
          console.error('Error parsing stored user:', e);
          localStorage.removeItem('user');
          setLoading(false);
          return;
        }
      }

      // Then verify token is still valid by fetching user data (async)
      if (storedToken) {
        try {
          const response = await authAPI.me();
          if (response.status === 'success') {
            setUser(response.user);
            localStorage.setItem('user', JSON.stringify(response.user));
          }
        } catch (err) {
          console.error('Token verification failed:', err);
          // Token is invalid, clear auth state
          setUser(null);
          setToken(null);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
        }
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  /**
   * Login user
   */
  const login = async (username, password) => {
    setError(null);
    try {
      const response = await authAPI.login(username, password);

      if (response.status === 'success') {
        setToken(response.token);
        setUser(response.user);

        // Store in localStorage
        localStorage.setItem('auth_token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));

        return { success: true, user: response.user };
      } else {
        const errorMessage = response.message || 'Login failed';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      let errorMessage = 'An error occurred during login';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.errors) {
        // Handle validation errors
        const errors = err.response.data.errors;
        errorMessage = Object.values(errors).flat().join(', ');
      }

      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Register new user
   * For clients: auto-login by storing returned token
   * For lawyers: no auto-login (requires admin approval)
   */
  const register = async (userData) => {
    setError(null);
    try {
      const response = await authAPI.register(userData);

      if (response.status === 'success') {
        // If token returned (client registration), auto-login
        if (response.token && response.user) {
          setToken(response.token);
          setUser(response.user);
          localStorage.setItem('auth_token', response.token);
          localStorage.setItem('user', JSON.stringify(response.user));
          return { success: true, autoLoggedIn: true, user: response.user };
        }
        // Lawyer registration - no auto-login
        return { success: true, autoLoggedIn: false, message: response.message };
      } else {
        const errorMessage = response.message || 'Registration failed';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      let errorMessage = 'An error occurred during registration';

      if (err.response?.data?.errors) {
        // Handle validation errors
        const errors = err.response.data.errors;
        errorMessage = Object.entries(errors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('\n');
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    try {
      // Call backend logout endpoint if user is logged in
      if (token) {
        await authAPI.logout();
      }
    } catch (err) {
      console.error('Logout API call failed:', err);
      // Continue with logout even if API call fails
    }

    // Clear local state
    setUser(null);
    setToken(null);
    setError(null);

    // Clear localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  };

  /**
   * Check if user has a specific role
   */
  const hasRole = (role) => {
    if (!user) return false;

    switch (role) {
      case 'lawyer':
        return user.is_lawyer === true;
      case 'client':
        return user.is_client === true;
      case 'enduser':
        return user.is_enduser === true;
      case 'admin':
        return user.is_staff === true || user.is_superuser === true;
      case 'affiliate':
        return user.is_affiliate === true;
      default:
        return false;
    }
  };

  /**
   * Get user's primary role (for routing)
   */
  const getPrimaryRole = () => {
    return getRoleFromUser(user);
  };

  /**
   * Check if user has access to a feature
   */
  const checkFeature = (featureCode) => {
    return hasFeature(user, featureCode);
  };

  const value = {
    user,
    token,
    loading,
    error,
    login,
    register,
    logout,
    hasRole,
    getPrimaryRole,
    checkFeature,
    isAuthenticated: !!token && !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default AuthContext;
